import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

// Deployment cutoff for "legacy entry" badge — entries created before this
// were saved with the pre-Phase-6 field structure.
export const MARKETER_LEGACY_CUTOFF = "2026-05-06T00:00:00Z";

export const MARKETER_STATUSES = [
  "work_in_progress",
  "completed",
  "waiting_for_approval",
] as const;
export type MarketerStatus = (typeof MARKETER_STATUSES)[number];
export const MARKETER_STATUS_LABEL: Record<MarketerStatus, string> = {
  work_in_progress: "Work in Progress",
  completed: "Completed",
  waiting_for_approval: "Waiting for Approval",
};

// SOURCE OF TRUTH: also maintained in lookup_items
// table. When adding new values, add to BOTH here
// and lookup_items, or the DB will accept it but
// the dropdown won't show it.
export const MARKETER_CATEGORIES = [
  "ad_campaign_publish",
  "ad_campaign_tracking",
  "product_research",
  "competitor_research",
  "creative_research",
  "script_writing",
  "meeting",
  "shopify_store_edit",
  "other",
] as const;
export type MarketerCategory = (typeof MARKETER_CATEGORIES)[number];
export const MARKETER_CATEGORY_LABEL: Record<MarketerCategory, string> = {
  ad_campaign_publish: "Ad Campaign — Publish",
  ad_campaign_tracking: "Ad Campaign — Tracking & Analysis",
  product_research: "Product Research",
  competitor_research: "Competitor Research",
  creative_research: "Creative Research",
  script_writing: "Script Writing",
  meeting: "Meeting",
  shopify_store_edit: "Shopify Store Edit",
  other: "Other",
};

// Categories that require the global Brand field
export const MARKETER_BRAND_CATEGORIES = new Set<MarketerCategory>([
  "ad_campaign_publish",
  "ad_campaign_tracking",
]);
// Categories that have a Status field
export const MARKETER_STATUS_CATEGORIES = new Set<MarketerCategory>([
  "ad_campaign_publish",
  "shopify_store_edit",
  "other",
]);
// Aggregate (Pattern B) — no status, no brand
export const MARKETER_AGGREGATE_CATEGORIES = new Set<MarketerCategory>([
  "product_research",
  "competitor_research",
  "creative_research",
]);

// SOURCE OF TRUTH: also maintained in lookup_items
// table. When adding new values, add to BOTH here
// and lookup_items, or the DB will accept it but
// the dropdown won't show it.
export const MARKETER_OTHER_TASK_TYPES = [
  "laptop_issue",
  "network_issue",
  "software_error",
  "meetings",
  "fun_friday",
  "others",
] as const;
export type MarketerOtherTaskType = (typeof MARKETER_OTHER_TASK_TYPES)[number];
export const MARKETER_OTHER_TASK_LABEL: Record<MarketerOtherTaskType, string> = {
  laptop_issue: "Laptop Issue",
  network_issue: "Network Issue",
  software_error: "Software Error",
  meetings: "Meetings",
  fun_friday: "Fun Friday / Celebration",
  others: "Others",
};

export const MARKETER_CORRECTION_STATUSES = ["pending", "approved", "fix"] as const;
export type MarketerCorrectionStatus = (typeof MARKETER_CORRECTION_STATUSES)[number];

export interface MarketerTask {
  id: string;
  user_id: string;
  task_date: string;
  task_category: MarketerCategory;
  brand: string;
  status: MarketerStatus;
  category_data: Record<string, unknown>;
  drive_link: string | null;
  notes: string | null;
  parent_task_id: string | null;
  correction_status: MarketerCorrectionStatus;
  correction_note: string | null;
  points: number;
  points_manually_set: boolean;
  created_at: string;
  updated_at: string;
}

export interface MarketerOtherTask {
  id: string;
  user_id: string;
  task_date: string;
  start_time: string;
  end_time: string;
  task_type: MarketerOtherTaskType;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface MarketerScript {
  id: string;
  user_id: string;
  script_title: string;
  product_name: string;
  brand: string;
  status: MarketerStatus;
  drive_link: string;
  submission_date: string;
  notes: string | null;
  correction_status: MarketerCorrectionStatus;
  correction_note: string | null;
  points: number;
  start_time: string | null;
  end_time: string | null;
  created_at: string;
  updated_at: string;
}

// ---------- Helpers ----------

const url = z.string().url("Must be a valid URL");
const optUrl = z.string().url("Must be a valid URL").optional().or(z.literal(""));
const optText = z.string().optional().or(z.literal(""));
const time = z.string().regex(/^\d{2}:\d{2}$/, "Required");

const timeSpentSchema = z
  .object({
    hours: z.coerce.number().int().min(0).default(0),
    minutes: z.coerce.number().int().min(0).max(59).default(0),
  })
  .refine((d) => d.hours * 60 + d.minutes > 0, {
    message: "Time spent must be greater than 0",
    path: ["minutes"],
  });

export function timeSpentMinutes(d: { hours?: number; minutes?: number } | undefined) {
  if (!d) return 0;
  return (d.hours ?? 0) * 60 + (d.minutes ?? 0);
}

export function formatDuration(totalMinutes: number) {
  if (!totalMinutes || totalMinutes < 0) return "0m";
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

export function durationFromTimes(start?: string, end?: string): number {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return 0;
  return Math.max(0, eh * 60 + em - (sh * 60 + sm));
}

const budgetSchema = z.object({
  amount: z.coerce.number().min(0),
  currency: z.enum(["INR", "USD"]).default("INR"),
});

// ---------- Per-category schemas (Phase 6) ----------

export const MARKETER_CATEGORY_SCHEMAS: Record<MarketerCategory, z.ZodType<Record<string, unknown>>> = {
  ad_campaign_publish: z
    .object({
      campaign_name: z.string().trim().min(1).max(200),
      ad_account: z.string().trim().min(1).max(120),
      budget: budgetSchema,
      ads_published: z.coerce.number().int().min(1, "Must be at least 1"),
      start_time: time,
      end_time: time,
      notes: optText,
    })
    .refine((d) => d.end_time > d.start_time, {
      message: "End time must be after start time",
      path: ["end_time"],
    }),
  ad_campaign_tracking: z
    .object({
      campaign_name: z.string().trim().min(1).max(200),
      ad_account: z.string().trim().min(1).max(120),
      changes_made: z.string().trim().min(1),
      start_time: time,
      end_time: time,
      notes: optText,
    })
    .refine((d) => d.end_time > d.start_time, {
      message: "End time must be after start time",
      path: ["end_time"],
    }),
  product_research: z
    .object({
      products_found: z.coerce.number().int().min(0),
      start_time: time,
      end_time: time,
      duration_minutes: z.coerce.number().int().min(0).optional(),
      platforms_used: z.array(z.string()).default([]),
      notes: optText,
    })
    .refine((d) => d.end_time > d.start_time, {
      message: "End time must be after start time",
      path: ["end_time"],
    }),
  competitor_research: z
    .object({
      competitors_found: z.coerce.number().int().min(0),
      start_time: time,
      end_time: time,
      duration_minutes: z.coerce.number().int().min(0).optional(),
      platforms_used: z.array(z.string()).default([]),
      notes: optText,
    })
    .refine((d) => d.end_time > d.start_time, {
      message: "End time must be after start time",
      path: ["end_time"],
    }),
  creative_research: z
    .object({
      total_references_found: z.coerce.number().int().min(0),
      start_time: time,
      end_time: time,
      duration_minutes: z.coerce.number().int().min(0).optional(),
      types: z.array(z.string()).default([]),
      doc_link: z.string().trim().url("Please add the sheet or doc link"),
      notes: optText,
    })
    .refine((d) => d.end_time > d.start_time, {
      message: "End time must be after start time",
      path: ["end_time"],
    }),
  // Script Writing routes through the bulk grid → marketer_scripts; this schema
  // is a no-op so that single-entry edits remain validatable if ever used.
  script_writing: z.object({
    note: optText,
  }),
  meeting: z
    .object({
      meeting_type: z.string().trim().min(1, "Required"),
      start_time: time,
      end_time: time,
      main_takeaway: z.string().trim().min(30, "Main takeaway must be at least 30 characters"),
    })
    .refine((d) => d.end_time > d.start_time, {
      message: "End time must be after start time",
      path: ["end_time"],
    }),
  shopify_store_edit: z
    .object({
      store_name: z.string().trim().min(1, "Required"),
      changes_made: z.string().trim().min(1),
      reason: optText,
      start_time: time,
      end_time: time,
    })
    .refine((d) => d.end_time > d.start_time, {
      message: "End time must be after start time",
      path: ["end_time"],
    }),
  other: z
    .object({
      description: z.string().trim().min(1),
      reference_link: optUrl,
      start_time: time,
      end_time: time,
      notes: optText,
    })
    .refine((d) => d.end_time > d.start_time, {
      message: "End time must be after start time",
      path: ["end_time"],
    }),
};

export const marketerTaskSchema = z
  .object({
    task_category: z.enum(MARKETER_CATEGORIES),
    brand: z.string().optional().default(""),
    status: z.enum(MARKETER_STATUSES).default("completed"),
    category_data: z.record(z.string(), z.unknown()).default({}),
    drive_link: optUrl,
    notes: optText,
    parent_task_id: z.string().uuid().nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (MARKETER_BRAND_CATEGORIES.has(val.task_category) && !val.brand) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Brand is required", path: ["brand"] });
    }
    const sch = MARKETER_CATEGORY_SCHEMAS[val.task_category];
    const result = sch.safeParse(val.category_data);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({
          ...issue,
          path: ["category_data", ...(issue.path as (string | number)[])],
        });
      }
    }
  });

export type MarketerTaskInput = z.infer<typeof marketerTaskSchema>;

export const marketerOtherTaskSchema = z
  .object({
    start_time: time,
    end_time: time,
    task_type: z.enum(MARKETER_OTHER_TASK_TYPES),
    notes: z.string().trim().min(1, "Required").max(1000),
  })
  .refine((d) => d.end_time > d.start_time, {
    message: "End time must be after start time",
    path: ["end_time"],
  });
export type MarketerOtherTaskInput = z.infer<typeof marketerOtherTaskSchema>;

export const marketerScriptBulkRowSchema = z
  .object({
    script_title: z.string().trim().min(1).max(200),
    product_name: z.string().trim().min(1).max(200),
    brand: z.enum(["Brand", "Viral"]),
    status: z.enum(MARKETER_STATUSES),
    drive_link: z.string().url(),
    start_time: time,
    end_time: time,
  })
  .refine((d) => d.end_time > d.start_time, {
    message: "End time must be after start time",
    path: ["end_time"],
  });
export type MarketerScriptBulkRow = z.infer<typeof marketerScriptBulkRowSchema>;

// ---------- Queries ----------

export async function fetchMyMarketerTasks(userId: string) {
  const { data, error } = await supabase
    .from("marketer_tasks")
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("task_date", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as MarketerTask[];
}

export async function fetchMyMarketerOtherTasks(userId: string) {
  const { data, error } = await supabase
    .from("marketer_other_tasks")
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("task_date", { ascending: false })
    .order("start_time", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as MarketerOtherTask[];
}

export async function fetchMyMarketerScripts(userId: string) {
  const { data, error } = await supabase
    .from("marketer_scripts")
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("submission_date", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as MarketerScript[];
}

// Autocomplete: fetch this user's published campaigns
export async function fetchMyPublishedCampaigns(userId: string) {
  const { data, error } = await supabase
    .from("marketer_tasks")
    .select("id, brand, category_data, task_date")
    .eq("user_id", userId)
    .eq("task_category", "ad_campaign_publish")
    .is("deleted_at", null)
    .order("task_date", { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as unknown as Array<{
    id: string;
    brand: string;
    task_date: string;
    category_data: Record<string, unknown>;
  }>;
}

/** For aggregate categories, derive duration_minutes from start/end before save. */
function withDerivedDuration(category: MarketerCategory, data: Record<string, unknown>): Record<string, unknown> {
  if (!MARKETER_AGGREGATE_CATEGORIES.has(category)) return data;
  const start = data.start_time as string | undefined;
  const end = data.end_time as string | undefined;
  if (start && end) {
    return { ...data, duration_minutes: durationFromTimes(start, end) };
  }
  return data;
}

export async function insertMarketerTask(userId: string, v: MarketerTaskInput) {
  const cd = withDerivedDuration(v.task_category, v.category_data);
  const { data, error } = await supabase
    .from("marketer_tasks")
    .insert({
      user_id: userId,
      task_category: v.task_category,
      brand: v.brand || "",
      status: v.status,
      category_data: cd as never,
      drive_link: v.drive_link || null,
      notes: v.notes || null,
      parent_task_id: v.parent_task_id ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return data as unknown as MarketerTask;
}

export async function updateMarketerTask(id: string, v: MarketerTaskInput) {
  const cd = withDerivedDuration(v.task_category, v.category_data);
  const { data, error } = await supabase
    .from("marketer_tasks")
    .update({
      task_category: v.task_category,
      brand: v.brand || "",
      status: v.status,
      category_data: cd as never,
      drive_link: v.drive_link || null,
      notes: v.notes || null,
      parent_task_id: v.parent_task_id ?? null,
    })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as MarketerTask;
}

export async function deleteMarketerTask(id: string) {
  const { error } = await supabase.from("marketer_tasks").delete().eq("id", id);
  if (error) throw error;
}

export async function insertMarketerOtherTask(userId: string, v: MarketerOtherTaskInput) {
  const { data, error } = await supabase
    .from("marketer_other_tasks")
    .insert({
      user_id: userId,
      start_time: v.start_time,
      end_time: v.end_time,
      task_type: v.task_type,
      notes: v.notes,
    })
    .select()
    .single();
  if (error) throw error;
  return data as unknown as MarketerOtherTask;
}

export async function updateMarketerOtherTask(id: string, v: MarketerOtherTaskInput) {
  const { data, error } = await supabase
    .from("marketer_other_tasks")
    .update({
      start_time: v.start_time,
      end_time: v.end_time,
      task_type: v.task_type,
      notes: v.notes,
    })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as MarketerOtherTask;
}

export async function deleteMarketerOtherTask(id: string) {
  const { error } = await supabase.from("marketer_other_tasks").delete().eq("id", id);
  if (error) throw error;
}

export async function bulkInsertMarketerScripts(rows: MarketerScriptBulkRow[]) {
  const { data, error } = await supabase.rpc("bulk_insert_marketer_scripts", {
    rows: rows as never,
  });
  if (error) throw error;
  return data as unknown as MarketerScript[];
}

export async function deleteMarketerScript(id: string) {
  const { error } = await supabase.from("marketer_scripts").delete().eq("id", id);
  if (error) throw error;
}

// ---------- Display helpers ----------

export function isCurrentMonth(dateStr: string): boolean {
  const d = new Date(dateStr);
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
}

export function isLegacyEntry(t: { created_at: string; task_category: MarketerCategory; category_data: Record<string, unknown> }) {
  if (new Date(t.created_at) >= new Date(MARKETER_LEGACY_CUTOFF)) return false;
  // Heuristic: if any new-shape required key exists, treat as not legacy
  const d = t.category_data || {};
  switch (t.task_category) {
    case "ad_campaign_publish":
      return !("ads_published" in d);
    case "ad_campaign_tracking":
      return !("changes_made" in d);
    case "product_research":
      return !("products_found" in d);
    case "competitor_research":
      return !("competitors_found" in d);
    case "creative_research":
      return !("total_references_found" in d);
    case "meeting":
      return !("main_takeaway" in d);
    case "shopify_store_edit":
      return !("store_name" in d);
    default:
      return false;
  }
}

function currencySymbol(c: string) {
  return c === "USD" ? "$" : "₹";
}

export function detailsSummary(t: MarketerTask): string {
  const d = t.category_data as Record<string, unknown>;
  const s = (k: string) => (d[k] as string | undefined) ?? "";
  switch (t.task_category) {
    case "ad_campaign_publish": {
      const b = (d.budget as { amount?: number; currency?: string } | undefined) ?? {};
      const amt = b.amount != null ? `${currencySymbol(b.currency ?? "INR")}${b.amount}` : "";
      return [s("campaign_name"), amt].filter(Boolean).join(" · ");
    }
    case "ad_campaign_tracking":
      return `${s("campaign_name")} — ${s("changes_made").slice(0, 40)}`;
    case "product_research": {
      const mins = aggregateDurationMinutes(d);
      return `${(d.products_found as number) ?? 0} products · ${formatDuration(mins)}`;
    }
    case "competitor_research": {
      const mins = aggregateDurationMinutes(d);
      return `${(d.competitors_found as number) ?? 0} competitors · ${formatDuration(mins)}`;
    }
    case "creative_research": {
      const mins = aggregateDurationMinutes(d);
      return `${(d.total_references_found as number) ?? 0} refs · ${formatDuration(mins)}`;
    }
    case "script_writing":
      return "Bulk scripts (see Scripts tab)";
    case "meeting": {
      const mins = durationFromTimes(s("start_time"), s("end_time"));
      return `${s("meeting_type")} · ${formatDuration(mins)}`;
    }
    case "shopify_store_edit":
      return `${s("store_name")} · ${s("changes_made").slice(0, 40)}`;
    case "other":
      return s("description").slice(0, 60);
    default:
      return "";
  }
}

// Returns total research minutes for a list of tasks (used by KPIs)
export function researchMinutes(tasks: MarketerTask[]): number {
  return tasks.reduce((sum, t) => {
    if (
      t.task_category === "product_research" ||
      t.task_category === "competitor_research" ||
      t.task_category === "creative_research"
    ) {
      return sum + aggregateDurationMinutes(t.category_data ?? {});
    }
    return sum;
  }, 0);
}

/** Compute duration minutes from either the new start/end shape or legacy time_spent. */
export function aggregateDurationMinutes(d: Record<string, unknown>): number {
  if (typeof d.duration_minutes === "number") return d.duration_minutes;
  if (typeof d.start_time === "string" && typeof d.end_time === "string") {
    return durationFromTimes(d.start_time, d.end_time);
  }
  if (d.time_spent) {
    return timeSpentMinutes(d.time_spent as { hours?: number; minutes?: number });
  }
  return 0;
}

// Marker for table rows that originated from marketer_scripts (merged into the marketer task list)
export type MarketerTaskRow = MarketerTask & { _source?: "task" | "script"; _scriptTitle?: string };

export function scriptToMarketerRow(s: MarketerScript): MarketerTaskRow {
  return {
    id: s.id,
    user_id: s.user_id,
    task_date: s.submission_date,
    task_category: "script_writing",
    brand: s.brand,
    status: s.status,
    category_data: {
      start_time: s.start_time ?? undefined,
      end_time: s.end_time ?? undefined,
    },
    drive_link: s.drive_link ?? null,
    notes: s.notes,
    parent_task_id: null,
    correction_status: s.correction_status,
    correction_note: s.correction_note,
    points: s.points,
    points_manually_set: false,
    created_at: s.created_at,
    updated_at: s.updated_at,
    _source: "script",
    _scriptTitle: s.script_title,
  };
}
